// src/services/cargoService.js
// Service for handling cargo company integrations and label generation

import jsPDF from 'jspdf';

// Mock cargo company configurations (in a real implementation, these would come from user settings)
const CARGO_COMPANIES = {
  'Sürat Kargo': {
    name: 'Sürat Kargo',
    apiEndpoint: 'https://api.suratkargo.com.tr',
    requiredFields: ['username', 'password', 'customerNumber'],
    labelFormats: ['PDF', 'ZPL', 'EPL']
  },
  'MNG Kargo': {
    name: 'MNG Kargo',
    apiEndpoint: 'https://api.mngkargo.com.tr',
    requiredFields: ['username', 'password'],
    labelFormats: ['PDF', 'ZPL']
  },
  'PTT Kargo': {
    name: 'PTT Kargo',
    apiEndpoint: 'https://api.pttkargo.com.tr',
    requiredFields: ['licenseKey'],
    labelFormats: ['PDF']
  },
  'Yurtiçi Kargo': {
    name: 'Yurtiçi Kargo',
    apiEndpoint: 'https://api.yurticikargo.com',
    requiredFields: ['username', 'password'],
    labelFormats: ['PDF', 'ZPL']
  }
};

// Add sanitize function for Turkish characters in PDFs
const sanitizeForPDF = (text) => {
  if (typeof text !== 'string') return text;
  return text.replace(/ı/g, 'i').replace(/İ/g, 'I').replace(/ğ/g, 'g').replace(/Ğ/g, 'G').replace(/ü/g, 'u').replace(/Ü/g, 'U').replace(/ş/g, 's').replace(/Ş/g, 'S').replace(/ö/g, 'o').replace(/Ö/g, 'O').replace(/ç/g, 'c').replace(/Ç/g, 'C');
};

// Function to generate mock label content based on format
const generateMockLabelContent = (cargoCompany, shipmentData, labelFormat) => {
  const content = `
KARGO ETİKETİ
============
Firma: ${cargoCompany}
Takip Numarası: ${shipmentData.trackingNumber}
Sipariş No: ${shipmentData.orderId}
Müşteri: ${shipmentData.customerName}
Adres: ${shipmentData.customerAddress || 'Belirtilmemiş'}
Telefon: ${shipmentData.customerPhone || 'Belirtilmemiş'}

Ürünler:
${shipmentData.items.map(item => `- ${item.name} (${item.quantity} adet)`).join('\n')}

${new Date().toLocaleString('tr-TR')}
  `.trim();

  return content;
};

// Function to create a downloadable file
const createDownloadableFile = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  return { url, blob };
};

// Mock function to simulate API call for label generation
// In a real implementation, this would make actual API calls to cargo companies
export const generateCargoLabel = async (cargoCompany, shipmentData, credentials, labelFormat = 'PDF') => {
  try {
    // Validate cargo company
    if (!CARGO_COMPANIES[cargoCompany]) {
      throw new Error(`Cargo company ${cargoCompany} is not supported`);
    }

    // Validate required credentials
    const requiredFields = CARGO_COMPANIES[cargoCompany].requiredFields;
    for (const field of requiredFields) {
      if (!credentials[field]) {
        throw new Error(`Missing required credential: ${field}`);
      }
    }

    // Validate shipment data
    if (!shipmentData.trackingNumber) {
      throw new Error('Tracking number is required');
    }

    if (!shipmentData.customerName) {
      throw new Error('Customer name is required');
    }

    if (!shipmentData.items || shipmentData.items.length === 0) {
      throw new Error('At least one item is required');
    }

    // In a real implementation, this would make an API call to the cargo company
    // For now, we'll simulate a successful response with actual file content
    console.log(`Generating ${labelFormat} label for ${cargoCompany}`, shipmentData, credentials);

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Generate mock label content
    const labelContent = generateMockLabelContent(cargoCompany, shipmentData, labelFormat);

    // Create downloadable file based on format
    let fileData;
    let mimeType;
    let extension;

    switch (labelFormat.toUpperCase()) {
      case 'ZPL':
        mimeType = 'application/zpl';
        extension = 'zpl';
        // For ZPL, we'll create a simple ZPL template
        fileData = `^XA^FO50,50^ADN,36,20^FD${cargoCompany}^FS
^FO50,150^ADN,24,12^FDTakip No: ${shipmentData.trackingNumber}^FS
^FO50,200^ADN,24,12^FDMüşteri: ${shipmentData.customerName}^FS
^FO50,250^ADN,24,12^FDAdres: ${shipmentData.customerAddress || 'Belirtilmemiş'}^FS
^FO50,300^ADN,24,12^FDTelefon: ${shipmentData.customerPhone || 'Belirtilmemiş'}^FS
^XZ`;
        break;
      case 'EPL':
        mimeType = 'application/epl';
        extension = 'epl';
        // For EPL, we'll create a simple EPL template
        fileData = `N
q812
Q128,24
B50,50,Q,r,8,4,40,B,"${shipmentData.trackingNumber}"
A50,150,0,3,1,1,N,"${cargoCompany}"
A50,200,0,2,1,1,N,"Takip No: ${shipmentData.trackingNumber}"
A50,250,0,2,1,1,N,"Müşteri: ${shipmentData.customerName}"
P1
`;
        break;
      default: // PDF - generate a proper 100x100mm PDF
        mimeType = 'application/pdf';
        extension = 'pdf';
        // For PDF format, we'll return a placeholder since the actual PDF generation happens in the frontend
        fileData = labelContent;
    }

    const filename = `kargo_etiketi_${shipmentData.trackingNumber}.${extension}`;
    const downloadableFile = createDownloadableFile(fileData, filename, mimeType);

    // Return mock label data with actual downloadable content
    return {
      success: true,
      labelData: fileData,
      trackingNumber: shipmentData.trackingNumber,
      format: labelFormat,
      downloadUrl: downloadableFile.url,
      filename: filename,
      mimeType: mimeType
    };
  } catch (error) {
    console.error('Error generating cargo label:', error);
    // Return error object instead of throwing to make it easier to handle in the UI
    return {
      success: false,
      message: error.message || 'Kargo etiketi oluşturulurken bir hata oluştu'
    };
  }
};

// Function to generate a 100x100mm cargo label PDF
export const generateCargoLabelPDF = async (companyInfo, order, shipment, products, cargoCompany, trackingNumber) => {
  try {
    // Create a 100x100mm PDF (standard sticker size)
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: [100, 100] // 100mm x 100mm
    });

    const getProduct = (productId) => products?.find(p => p.id === productId);

    // Add cargo company logo/name at the top
    doc.setFontSize(20); // Increased font size from 18 to 20
    doc.setTextColor(0, 0, 0);
    doc.text(sanitizeForPDF(cargoCompany), 50, 12, { align: 'center' });
    doc.line(5, 15, 95, 15);

    // Sender Information (Company)
    doc.setFontSize(14); // Increased font size from 12 to 14
    doc.setFont('helvetica', 'bold');
    doc.text(sanitizeForPDF("GÖNDERİCİ"), 5, 22);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12); // Increased font size from 10 to 12
    doc.text(sanitizeForPDF(companyInfo?.companyName || "Firma Adı"), 5, 28);

    // Sender Cargo Customer Number (if available in credentials)
    // Removed customer code (Cari Kod) as per user request
    // const savedCredentials = JSON.parse(localStorage.getItem('cargoCredentials') || '[]');
    // const companyCredentials = savedCredentials.find(cred => cred.company === cargoCompany);
    // if (companyCredentials && companyCredentials.customerNumber) {
    //   doc.setFont('helvetica', 'bold');
    //   doc.text(sanitizeForPDF("Müşteri No:"), 5, 34);
    //   doc.setFont('helvetica', 'normal');
    //   doc.text(sanitizeForPDF(companyCredentials.customerNumber), 25, 34);
    // }

    // Recipient Information (Customer)
    doc.setFontSize(14); // Increased font size from 12 to 14
    doc.setFont('helvetica', 'bold');
    doc.text(sanitizeForPDF("ALICI"), 5, 34);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12); // Increased font size from 10 to 12
    doc.text(sanitizeForPDF(order.customerName || ""), 5, 40);

    // Complete address information
    if (order.customerAddress) {
      doc.setFont('helvetica', 'bold');
      doc.text(sanitizeForPDF("Adres:"), 5, 48);
      doc.setFont('helvetica', 'normal');
      // Split address into multiple lines if too long
      const addressLines = doc.splitTextToSize(sanitizeForPDF(order.customerAddress), 90);
      for (let i = 0; i < Math.min(addressLines.length, 3); i++) {
        doc.text(addressLines[i], 5, 52 + (i * 4));
      }
    }

    // All phone numbers
    if (order.customerPhone) {
      doc.setFont('helvetica', 'bold');
      doc.text(sanitizeForPDF("Telefon:"), 5, 68);
      doc.setFont('helvetica', 'normal');
      doc.text(sanitizeForPDF(order.customerPhone), 25, 68);
    }

    // Shipment Information
    doc.setFontSize(14); // Increased font size from 12 to 14
    doc.setFont('helvetica', 'bold');
    doc.text(sanitizeForPDF("KARGO BİLGİLERİ"), 5, 76);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12); // Increased font size from 10 to 12
    doc.text(`${sanitizeForPDF("Takip No")}: ${sanitizeForPDF(trackingNumber)}`, 5, 81);
    doc.text(`${sanitizeForPDF("Sipariş No")}: ${order.id.substring(0, 8).toUpperCase()}`, 5, 86);
    doc.text(`${sanitizeForPDF("Tarih")}: ${new Date().toLocaleDateString('tr-TR')}`, 5, 91);

    // Items Table
    const startY = 99;
    doc.setFontSize(12); // Increased font size from 10 to 12
    doc.setFont('helvetica', 'bold');
    doc.text(sanitizeForPDF("ÜRÜNLER"), 5, startY);

    let currentY = startY + 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11); // Increased font size from 9 to 11

    // Use shipment items if available, otherwise use form items
    const itemsToDisplay = shipment?.items || [];
    if (itemsToDisplay.length > 0) {
      itemsToDisplay.forEach((item, index) => {
        if (index < 6) { // Reduced to 6 items to avoid overflow on small label with larger fonts
          const product = getProduct(item.productId);
          const productBarcode = product?.barcode || '-';
          const lineText = `${productBarcode} - ${sanitizeForPDF(item.name || "")} (${sanitizeForPDF(item.quantity.toString())} adet)`;
          doc.text(lineText, 5, currentY);
          currentY += 4;
        }
      });

      if (itemsToDisplay.length > 6) {
        doc.text(`${sanitizeForPDF("... ve")} ${itemsToDisplay.length - 6} ${sanitizeForPDF("diğer ürün")}`, 5, currentY);
      }
    } else {
      doc.text(sanitizeForPDF("Ürün bilgisi bulunamadı"), 5, currentY);
    }

    // Generate filename
    const customerName = (order.customerName || 'Musteri')
      .replace(/[^a-zA-ZğüşıöçĞÜŞİÖÇ0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 15);
    const orderNumber = order.id ? order.id.substring(0, 6).toUpperCase() : 'XXXXXX';
    const currentDate = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
    const filename = `${customerName}-${orderNumber}-${currentDate}-kargo-etiketi.pdf`;

    // Return the PDF document and filename
    return {
      doc: doc,
      filename: filename
    };
  } catch (error) {
    console.error("Kargo etiketi PDF oluşturulurken hata:", error);
    throw new Error("Kargo etiketi PDF'i oluşturulurken bir hata oluştu.");
  }
};

// Function to validate cargo company credentials
export const validateCredentials = async (cargoCompany, credentials) => {
  try {
    // Validate cargo company
    if (!CARGO_COMPANIES[cargoCompany]) {
      throw new Error(`Cargo company ${cargoCompany} is not supported`);
    }

    // Validate required fields are not empty
    const requiredFields = CARGO_COMPANIES[cargoCompany].requiredFields;
    for (const field of requiredFields) {
      if (!credentials[field] || credentials[field].trim() === '') {
        throw new Error(`${field} alanı boş bırakılamaz`);
      }
    }

    // In a real implementation, this would make an API call to validate credentials
    // For now, we'll simulate a successful validation
    console.log(`Validating credentials for ${cargoCompany}`, credentials);

    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 500));

    // Return mock validation result
    return {
      valid: true,
      message: 'Kimlik bilgileri başarıyla doğrulandı'
    };
  } catch (error) {
    console.error('Error validating credentials:', error);
    return {
      valid: false,
      message: error.message || 'Kimlik bilgileri doğrulanırken bir hata oluştu'
    };
  }
};

// Function to get supported cargo companies
export const getSupportedCargoCompanies = () => {
  return Object.values(CARGO_COMPANIES);
};

// Function to get label formats for a cargo company
export const getLabelFormats = (cargoCompany) => {
  if (CARGO_COMPANIES[cargoCompany]) {
    return CARGO_COMPANIES[cargoCompany].labelFormats;
  }
  return ['PDF']; // Default format
};

const cargoService = {
  generateCargoLabel,
  validateCredentials,
  getSupportedCargoCompanies,
  getLabelFormats,
  generateCargoLabelPDF // Export the new PDF generation function
};

export default cargoService;
